import { config } from "../../config";

const delegateExchangeEndpoint = "/auth/delegate/exchange";

export interface DelegateTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

// Single-flight guard: concurrent callers (e.g. parallel RTK Query requests
// firing on mount) must share ONE in-flight exchange. The delegation token has
// a limited `max_uses`, and each exchange increments its server-side use_count;
// without this, a burst of concurrent requests would each trigger a separate
// exchange, burning multiple uses at once and quickly exhausting the token.
let inFlight: { token: string; promise: Promise<DelegateTokenResponse> } | null =
  null;

/**
 * Exchange a one-time delegation token (apiv5) for a short-lived, refresh-less
 * access token.
 *
 * The launch URL carries a delegation token (`?token=`) minted by user-space or
 * by the back office (mail flow). The webview exchanges it once here, then uses
 * the resulting access_token as the Bearer for all api-payment calls.
 *
 * Concurrent calls for the same token share a single network request so we don't
 * burn the token's limited `max_uses` with redundant exchanges.
 *
 * @param token The opaque delegation token from the launch URL.
 * @returns The delegated access token payload.
 * @throws If the exchange fails (token expired, revoked, used, or network error).
 */
export const exchangeDelegationToken = (
  token: string
): Promise<DelegateTokenResponse> => {
  if (inFlight && inFlight.token === token) {
    return inFlight.promise;
  }

  const promise = (async () => {
    const response = await fetch(
      config.REACT_APP_IDENTITY_API_HOST + delegateExchangeEndpoint,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ token }),
      }
    );
    if (response.status !== 200) {
      throw new Error(`Delegate exchange failed with status ${response.status}`);
    }
    return response.json();
  })();

  inFlight = { token, promise };
  // Clear the guard once settled so a later (post-expiry) exchange can run.
  // Use then(onSettled, onSettled) rather than finally(): finally() returns a
  // promise that re-rejects on failure, and since nobody observes that derived
  // promise it would surface as an unhandled rejection. Here the onRejected
  // handler swallows the error in the cleanup branch, while the caller still
  // receives the original (rejecting) `promise`.
  const clearGuard = () => {
    if (inFlight?.promise === promise) {
      inFlight = null;
    }
  };
  promise.then(clearGuard, clearGuard);

  return promise;
};
